import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HelpCircle, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Compact FAQ preview — top N items + "see all" link.
 *  Use anywhere you want to give visitors quick reassurance without navigating away. */
export const FAQEmbed = ({ limit = 3, variant = 'light' }) => {
  const { t, lang } = useI18n();
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/faqs`).then(r => setFaqs(r.data || [])).catch(() => {});
  }, []);

  const visible = faqs.slice(0, limit);
  if (visible.length === 0) return null;

  const isDark = variant === 'dark';
  const baseText = isDark ? 'text-slate-100' : 'text-slate-900';
  const subText = isDark ? 'text-slate-400' : 'text-slate-600';
  const cardBg = isDark ? 'bg-slate-800/60 hover:bg-slate-800' : 'bg-white hover:bg-slate-50';
  const cardBorder = isDark ? 'border-slate-700' : 'border-slate-200';

  const q = (item) => (lang === 'no' && item.question_no) ? item.question_no : item.question_en;
  const a = (item) => (lang === 'no' && item.answer_no) ? item.answer_no : item.answer_en;

  return (
    <div data-testid="faq-embed" className="w-full">
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
        <h3 className={`text-lg sm:text-xl font-bold ${baseText} font-['Outfit']`}>
          {t('faq.embed_title')}
        </h3>
      </div>
      <div className="space-y-2">
        {visible.map(item => {
          const isOpen = openId === item.id;
          return (
            <div
              key={item.id}
              data-testid={`faq-embed-item-${item.id}`}
              className={`rounded-2xl border ${cardBorder} ${cardBg} overflow-hidden transition`}
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className={`w-full flex items-start justify-between gap-3 px-5 py-4 text-left ${baseText}`}
              >
                <span className="font-semibold text-sm sm:text-base">{q(item)}</span>
                {isOpen
                  ? <ChevronUp className={`w-4 h-4 flex-shrink-0 mt-1 ${subText}`} />
                  : <ChevronDown className={`w-4 h-4 flex-shrink-0 mt-1 ${subText}`} />}
              </button>
              {isOpen && (
                <div className={`px-5 pb-5 text-sm leading-relaxed ${subText}`}>
                  {a(item)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {faqs.length > limit && (
        <div className="mt-4 text-right">
          <Link
            to="/faq"
            data-testid="faq-embed-see-all"
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-700 hover:text-amber-900'}`}
          >
            {t('faq.embed_cta')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default FAQEmbed;
